// Helper function to parse boolean from string
const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean => {
  if (value === undefined || value === null) return defaultValue;
  return value.toLowerCase() === 'true';
};

// Helper function to parse array from comma-separated string
const parseArray = (value: string | undefined, defaultValue: string[]): string[] => {
  if (!value) return defaultValue;
  return value.split(',').map(item => item.trim());
};

/**
 * Auth-branch resolution (CAPA F273).
 *
 * App.tsx picks its auth branch from this pair, and only `okta && !oidc` mounted the
 * HIPAA inactivity warning. These two defaults shipped as the exact inverse of that
 * pair — okta:false / oidc:true — so stock configuration produced sessions that ended
 * with no countdown and no chance to stay signed in. Two env vars had to be set
 * correctly for a HIPAA control to exist at all.
 *
 * The fix is to stop declaring the branch and DERIVE it (RD 2026-08-06 — make it work
 * without the env var). A deployment must already supply a real issuer and client ID for
 * a provider to authenticate anyone, so the configuration already carries the answer; the
 * booleans were redundant state whose only power was to disagree with it. They are now an
 * optional override, honoured when supplied, so no existing deployment changes behaviour.
 *
 * SessionInactivityGuard makes the warning branch-independent regardless of how this
 * resolves — the derivation is defence in depth, not the control itself.
 */
/**
 * Is a value a real setting, or the template placeholder / an unfilled default?
 * The placeholders in this file are `<REACT_APP_*>`, `yourdomain.okta.com` and the
 * `xxxx-xxxxx-xxxxx` authority — none of which can authenticate anyone.
 */
const isConfigured = (value: string | undefined): boolean => {
  if (!value) return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith('<') && v.endsWith('>')) return false;
  if (v.includes('yourdomain')) return false;
  if (v.includes('xxxx')) return false;
  return true;
};

const resolveAuthFlags = (env: Record<string, string | undefined>) => {
  // DERIVED, not declared. A deployment already has to supply a real issuer + client ID
  // for a provider to function at all, so the configuration ALREADY says which provider
  // this is. A separate boolean is redundant state whose only power is to contradict it
  // — which is exactly what happened: the flags defaulted to a branch whose credentials
  // were never configured.
  const oktaConfigured =
    isConfigured(env.REACT_APP_OKTA_CLIENT_ID) && isConfigured(env.REACT_APP_OKTA_ISSUER);
  const oidcConfigured =
    isConfigured(env.REACT_APP_OIDC_CLIENT_ID) && isConfigured(env.REACT_APP_OIDC_AUTHORITY);

  // Explicit flags remain an OVERRIDE, honoured only when actually supplied. Any existing
  // deployment that sets them keeps precisely today's behaviour — this change can only
  // affect a deployment that sets neither, which is the case that used to resolve wrong.
  const oktaOverride = env.REACT_APP_OKTA_ENABLED;
  const oidcOverride = env.REACT_APP_OIDC_ENABLED;

  // Okta wins a tie: it is the live clinical provider app, and the OIDC/Azure branch is
  // the special case (as `aida` already declares itself explicitly).
  let oktaEnabled = oktaOverride !== undefined ? parseBoolean(oktaOverride, true) : oktaConfigured;
  let oidcEnabled =
    oidcOverride !== undefined
      ? parseBoolean(oidcOverride, false)
      : oidcConfigured && !oktaConfigured;

  // Nothing configured and nothing declared: fall back to Okta rather than to the
  // unauthenticated branch, so a misconfigured deployment lands on a login page instead
  // of silently serving the app with no auth at all.
  if (!oktaEnabled && !oidcEnabled) {
    console.warn(
      '[environment] No auth provider is configured or declared — neither Okta ' +
        '(REACT_APP_OKTA_CLIENT_ID + REACT_APP_OKTA_ISSUER) nor OIDC ' +
        '(REACT_APP_OIDC_CLIENT_ID + REACT_APP_OIDC_AUTHORITY). Defaulting to Okta so the ' +
        'app does not serve unauthenticated.'
    );
    oktaEnabled = true;
  }

  // Warn — do not throw. A misconfigured pair should announce itself, but hard-failing
  // the bundle would break an existing deployment on first load, with no operator present
  // to read the error (RD-disposed 2026-08-06: warn is sufficient).
  if (oktaEnabled && oidcEnabled) {
    console.warn(
      '[environment] Both Okta and OIDC resolved enabled. App.tsx checks OIDC first, so ' +
        'the Okta branch is unreachable. Set exactly one of REACT_APP_OKTA_ENABLED / ' +
        'REACT_APP_OIDC_ENABLED, or configure only one provider.'
    );
  }

  return { oktaEnabled, oidcEnabled };
};

// Get environment variables from window._env_ or use defaults
const getEnv = () => {
  const env = (window as any)._env_ || {};
  const { oktaEnabled, oidcEnabled } = resolveAuthFlags(env);

  return {
    production: parseBoolean(env.REACT_APP_PRODUCTION, false),
    webservices: {
      endpoint: env.REACT_APP_API_ENDPOINT || 'http://localhost:5000',
      elation_endpoint: 'https://xxxxx.elationemr.com',
    },
    okta: {
      isEnabled: oktaEnabled,
      issuer: env.REACT_APP_OKTA_ISSUER || 'https://yourdomain.okta.com/oauth2/default',
      clientId: env.REACT_APP_OKTA_CLIENT_ID || '<REACT_APP_OKTA_CLIENT_ID>',
      baseUrl: env.REACT_APP_OKTA_BASE_URL || 'https://yourdomain.okta.com',
      groups: parseArray(env.REACT_APP_OKTA_GROUPS, ['PROVIDER', 'NAVIGATOR']),
      scopes: parseArray(env.REACT_APP_OKTA_SCOPES, ['offline_access']),
      providerGroup: parseArray(env.REACT_APP_OKTA_PROVIDER_GROUP, ['PROVIDER']),
      navigatorGroup: parseArray(env.REACT_APP_OKTA_NAVIGATOR_GROUP, ['NAVIGATOR']),
    },
    oidc: {
      isEnabled: oidcEnabled,
      clientId: env.REACT_APP_OIDC_CLIENT_ID || '<REACT_APP_OIDC_CLIENT_ID>',
      authority: env.REACT_APP_OIDC_AUTHORITY || 'https://login.microsoftonline.com/xxxx-xxxxx-xxxxx/v2.0',
      redirectUri: env.REACT_APP_OIDC_REDIRECT_URI || 'http://localhost:3000/login/callback',
    },
    aida: {
      isEnabled: parseBoolean(env.REACT_APP_AIDA_ENABLED, false),
    },
    epic: {
      PROVIDER_CLIENTID: env.REACT_APP_EPIC_PROVIDER_CLIENTID || '<REACT_APP_EPIC_PROVIDER_CLIENTID>',
    },
    express: {
      isEnabled: parseBoolean(env.REACT_APP_EXPRESS_ENABLED, true),
    },
    gateway: {
      // Default to localhost so a missing env var fails loud locally instead of
      // silently pointing a higher environment at the real prod gateway and
      // corrupting its data. Every deployed environment MUST set
      // REACT_APP_GATEWAY_ENDPOINT explicitly.
      endpoint: env.REACT_APP_GATEWAY_ENDPOINT || 'http://localhost:8080',
      // Force off in AIDA mode; otherwise honor the configured value.
      enabled: parseBoolean(env.REACT_APP_AIDA_ENABLED, false) ? false : parseBoolean(env.REACT_APP_GATEWAY_ENABLED, true),
    },
    taskList: {
      isEnabled: true,
    },
  };
};

export const environment = getEnv();